import { useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { 
  ArrowLeft,
  Users, 
  MapPin,
  Calendar,
  Plus,
  Shield,
  ExternalLink
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useAuth as useAuthHook } from '../hooks/useAuth';
import { clubsApi } from '../lib/api/clubs';
import TopBar from '../components/TopBar';
import { createPlatformAdminClubsMobileMenu } from '../components/MobileContextMenu';
import Button from '../components/Button';

export default function PlatformAdminClubsPage() {
  const navigate = useNavigate();
  const { me } = useAuth();
  const { logout } = useAuthHook();

  // Redirect non-platform-admins
  useEffect(() => {
    if (me && !me.is_platform_admin) {
      navigate('/');
    }
  }, [me, navigate]);

  // Fetch all clubs for platform admin
  const { data: clubsData, isLoading } = useQuery({
    queryKey: ['admin', 'clubs'],
    queryFn: () => clubsApi.listAllClubs(),
    enabled: !!me?.is_platform_admin,
  });
  
  // Create mobile menu configuration
  const adminClubsMenu = createPlatformAdminClubsMobileMenu(navigate, logout);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short', 
      day: 'numeric'
    });
  };

  const getVisibilityBadge = (visibility: string) => {
    const colors = {
      public: 'bg-green-100 text-green-800',
      invite_only: 'bg-yellow-100 text-yellow-800',
      private: 'bg-red-100 text-red-800'
    };
    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${colors[visibility as keyof typeof colors] || 'bg-oro-surface-alt text-text-secondary'}`}>
        {visibility.replace('_', ' ')}
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-oro-bg">
      <TopBar title="Platform Admin" mobileMenu={adminClubsMenu} />
      
      {/* Header */}
      <div className="bg-oro-surface border-b border-oro-border">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button
                variant="ghost"
                onClick={() => navigate('/platform-admin/home')}
                className="p-2"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="text-3xl font-bold text-text-primary">All Clubs</h1>
                <div className="flex items-center space-x-2 text-sm text-text-secondary">
                  <Shield className="w-4 h-4" />
                  <span>Platform Admin View</span>
                </div>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <Button
                onClick={() => navigate('/clubs/create')}
                className="inline-flex items-center"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create Club
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {isLoading ? (
          <div className="space-y-4">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="bg-oro-surface rounded-lg shadow p-6 animate-pulse">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="h-6 bg-oro-surface-alt rounded w-1/3 mb-2"></div>
                    <div className="h-4 bg-oro-surface-alt rounded w-1/2 mb-4"></div>
                    <div className="flex items-center space-x-4">
                      <div className="h-4 bg-oro-surface-alt rounded w-24"></div>
                      <div className="h-4 bg-oro-surface-alt rounded w-20"></div>
                    </div>
                  </div>
                  <div className="h-8 bg-oro-surface-alt rounded w-20"></div>
                </div>
              </div>
            ))}
          </div>
        ) : !clubsData || clubsData.length === 0 ? (
          <div className="text-center py-12">
            <Shield className="w-12 h-12 text-text-muted mx-auto mb-4" />
            <h2 className="text-xl font-medium text-text-primary mb-2">No Clubs Found</h2>
            <p className="text-text-secondary mb-6">There are no clubs on the platform yet.</p>
            <Button onClick={() => navigate('/clubs/create')}>
              <Plus className="w-4 h-4 mr-2" />
              Create First Club
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {clubsData.map((club) => (
              <div key={club.id} className="bg-oro-surface rounded-lg shadow hover:shadow-md transition-shadow">
                <div className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start space-x-3">
                        {club.hero_image_url && (
                          <img 
                            src={club.hero_image_url}
                            alt={`${club.name} logo`}
                            className="w-16 h-16 rounded-lg object-cover shrink-0"
                          />
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center space-x-2 mb-1">
                            <Link 
                              to={`/platform-admin/clubs/${club.slug}`}
                              className="text-xl font-semibold text-text-primary hover:text-blue-600 truncate"
                            >
                              {club.name}
                            </Link>
                            {getVisibilityBadge(club.visibility)}
                          </div>
                          
                          {club.description && (
                            <p className="text-text-secondary mb-3 line-clamp-2">{club.description}</p>
                          )}
                          
                          <div className="flex items-center space-x-6 text-sm text-text-muted">
                            <div className="flex items-center space-x-1">
                              <MapPin className="w-4 h-4" />
                              <span>{club.city}, {club.state}</span>
                            </div>
                            <div className="flex items-center space-x-1">
                              <Users className="w-4 h-4" />
                              <span>{club.member_count || 0} members</span>
                            </div>
                            <div className="flex items-center space-x-1">
                              <Calendar className="w-4 h-4" />
                              <span>Created {formatDate(club.created_at)}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-2 ml-4">
                      <Link
                        to={`/clubs/${club.slug}`}
                        className="inline-flex items-center px-3 py-2 border border-oro-border shadow-sm text-sm leading-4 font-medium rounded-md text-text-secondary bg-oro-surface hover:bg-oro-surface-alt"
                      >
                        <ExternalLink className="w-4 h-4 mr-1" />
                        Public
                      </Link>
                      <Link
                        to={`/platform-admin/clubs/${club.slug}`}
                        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                      >
                        <Shield className="w-4 h-4 mr-1" />
                        Admin View
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Summary Stats */}
        {clubsData && clubsData.length > 0 && (
          <div className="mt-8 bg-oro-surface rounded-lg shadow p-6">
            <h2 className="text-lg font-medium text-text-primary mb-4">Platform Summary</h2>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600">{clubsData.length}</div>
                <div className="text-sm text-text-secondary">Total Clubs</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">
                  {clubsData.reduce((sum, club) => sum + (club.member_count || 0), 0)}
                </div>
                <div className="text-sm text-text-secondary">Total Members</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-purple-600">
                  {clubsData.filter(club => club.visibility === 'public').length}
                </div>
                <div className="text-sm text-text-secondary">Public Clubs</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-orange-600">
                  {clubsData.filter(club => club.visibility === 'invite_only').length}
                </div>
                <div className="text-sm text-text-secondary">Invite Only</div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}